import { Alert, Button, DatePicker, Pagination, Select, Table } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { TbFolderCog } from 'react-icons/tb'
import { Link, useSearchParams } from 'react-router-dom'

import { withDiskFeature, formatBytes } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_ADMIN_AUDIT_EVENTS, DISK_ADMIN_AUDIT_EVENT_LABELS } from '../Contracts/DiskAdmin'
import { DiskAdminShell } from '../Components/DiskAdminShell'
import { PersonAvatar } from '../Components/StorageName'
import { auditDetails } from '../Helpers/diskAdminAudit'
import { formatDateTime } from '../Helpers/diskAdminFormat'
import { diskAdminStorageUrl } from '../Helpers/diskAdminRoutes'
import { AUDIT_PAGE_SIZE, diskAdminAuditStore as store } from '../Stores/DiskAdminAuditStore'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import type { DiskAdminAuditEntry } from '../Contracts/DiskAdmin'
import type { TableColumnsType } from 'antd'
import type { HTMLAttributes } from 'react'

import styles from '../Components/DiskAdmin.module.css'

/** «Система» — дії політики без людини (`actor = null`). */
const SYSTEM = 'Система'

const columns: TableColumnsType<DiskAdminAuditEntry> = [
    {
        key: 'occurredAt',
        title: 'Коли',
        width: 160,
        render: (_, entry) => <span className={classNames(styles['num'])}>{formatDateTime(entry.occurredAt)}</span>,
    },
    {
        key: 'actor',
        title: 'Хто',
        width: 220,
        render: (_, entry) =>
            entry.actor ? (
                <span className={classNames(styles['nameCell'])}>
                    <PersonAvatar person={entry.actor} size={24} />
                    <span className={classNames(styles['nameTitle'])} title={entry.actor.name ?? ''}>
                        {entry.actor.name}
                    </span>
                </span>
            ) : (
                SYSTEM
            ),
        ellipsis: true,
    },
    {
        key: 'event',
        title: 'Подія',
        render: (_, entry) => (
            <span>
                <span className={classNames(styles['nameTitle'])}>{entry.eventLabel}</span>
                <div className={classNames(styles['details'])}>{auditDetails(entry, formatBytes)}</div>
                {entry.reason ? <div className={classNames(styles['details'])}>Причина: {entry.reason}</div> : null}
            </span>
        ),
    },
    {
        key: 'storage',
        title: 'Диск',
        width: 240,
        responsive: ['md'],
        render: (_, entry) =>
            entry.storage?.uuid ? (
                <Link to={diskAdminStorageUrl(entry.storage.uuid)} title={entry.storage.title ?? ''}>
                    {entry.storage.title ?? 'Диск'}
                </Link>
            ) : (
                <span className={classNames(styles['nameSub'])}>уся компанія</span>
            ),
        ellipsis: true,
    },
]

/**
 * Журнал адміністратора `/disk/admin/audit` (`DISK-006` §2.4, наряд Ф7):
 * кожне втручання — один запис, новіші вперед. Фільтри — період, хто,
 * подія, диск (`?storage=` з картки диска). Лише читання: змінювати чи
 * видаляти записи API не дає.
 */
const AuditPage = observer(() => {
    const [params] = useSearchParams()
    const storageParam = params.get('storage')
    const [actors, setActors] = useState<Map<string, string>>(new Map())

    useEffect(() => {
        store.resetFilter(storageParam)
    }, [storageParam])

    // «Хто» — люди, що вже траплялися в журналі (окремого переліку адміністраторів API не дає)
    useEffect(() => {
        setActors((known) => {
            const next = new Map(known)

            for (const entry of store.items) if (entry.actor?.uuid) next.set(entry.actor.uuid, entry.actor.name ?? '—')

            return next.size === known.size ? known : next
        })
    }, [store.items])

    const storageTitle = useMemo(
        () => store.items.find((entry) => entry.storage?.uuid === store.filter.storage)?.storage?.title ?? null,
        [store.items, store.filter.storage]
    )

    const { filter } = store

    return (
        <DiskAdminShell tab="audit" documentTitle="Журнал — адміністрування диска" eyebrow="Кожне втручання адміністратора й дії політики">
            <div className={classNames(styles['filters'])} data-testid="disk-admin-audit-filters">
                <DatePicker.RangePicker
                    aria-label="Період"
                    format="DD.MM.YYYY"
                    value={[filter.from ? dayjs(filter.from) : null, filter.to ? dayjs(filter.to) : null]}
                    onChange={(range) =>
                        store.setFilter({
                            from: range?.[0] ? range[0].format('YYYY-MM-DD') : null,
                            to: range?.[1] ? range[1].format('YYYY-MM-DD') : null,
                        })
                    }
                    allowEmpty={[true, true]}
                />
                <Select
                    aria-label="Хто"
                    allowClear
                    placeholder="Хто"
                    style={{ width: 240 }}
                    value={filter.actor}
                    onChange={(value: string | undefined) => store.setFilter({ actor: value ?? null })}
                    options={[...actors].map(([value, label]) => ({ value, label }))}
                />
                <Select
                    aria-label="Подія"
                    allowClear
                    placeholder="Подія"
                    style={{ width: 280 }}
                    value={filter.event}
                    onChange={(value: string | undefined) => store.setFilter({ event: value ?? null })}
                    options={DISK_ADMIN_AUDIT_EVENTS.map((value) => ({ value, label: DISK_ADMIN_AUDIT_EVENT_LABELS[value] }))}
                    data-testid="disk-admin-audit-event"
                />
                {filter.storage ? (
                    <Button onClick={() => store.setFilter({ storage: null })}>
                        Диск: {storageTitle ?? 'вибраний'} ×
                    </Button>
                ) : null}
                {store.hasFilter ? <Button onClick={() => store.resetFilter()}>Скинути фільтри</Button> : null}
            </div>

            {store.error !== null ? <Alert type="error" showIcon title={store.error} /> : null}

            <Table<DiskAdminAuditEntry>
                rowKey="uuid"
                size="middle"
                columns={columns}
                dataSource={store.items}
                loading={store.isLoading}
                pagination={false}
                locale={{ emptyText: store.hasFilter ? 'Нічого не знайдено — змініть фільтр' : 'Журнал порожній' }}
                onRow={(entry) => ({ 'data-event': entry.event }) as HTMLAttributes<HTMLElement>}
                data-testid="disk-admin-audit"
            />

            {store.total > AUDIT_PAGE_SIZE ? (
                <Pagination
                    align="end"
                    current={store.page}
                    pageSize={AUDIT_PAGE_SIZE}
                    total={store.total}
                    showSizeChanger={false}
                    showTotal={(total, [from, to]) => `${from}–${to} із ${total}`}
                    onChange={(page) => store.setPage(page)}
                />
            ) : null}
        </DiskAdminShell>
    )
})

export const pageConfig: IPageConfig = {
    key: 'Disk.AdminAudit',
    route: '/disk/admin/audit',
    title: 'Журнал — адміністрування диска',
    icon: <TbFolderCog />,
}

export default withDiskFeature(AuditPage)
